/**
 * COMPLETION RULES REST client (M1). Server-authoritative endpoints
 * outside of sync (same pattern as `assessment/api.ts`): student progress,
 * roster matrix, effective config, and the validated+gated write-paths.
 */
import { apiJson } from "@/lib/rest";
import type { CompletionConfig } from "@space/shared";

export interface CriterionBreakdownDTO {
  key: string;
  met: boolean;
  current: number;
  target: number;
  detail: string;
  params: Record<string, unknown>;
}

export interface CompletionStatusDTO {
  passed: boolean;
  configured: boolean;
  criteria: CriterionBreakdownDTO[];
}

export interface CompletionReportRow {
  user_id: string;
  name: string | null;
  email: string | null;
  final_display: string;
  passed: boolean;
  criteria: CriterionBreakdownDTO[];
}

export interface CompletionReportDTO {
  cohort_id: string;
  configured: boolean;
  rows: CompletionReportRow[];
}

export interface CompletionConfigDTO {
  config: CompletionConfig;
  configured: boolean;
}

const enc = encodeURIComponent;

/** The OWN student's completion progress in a cohort. */
export async function fetchMyCompletion(cohortId: string): Promise<CompletionStatusDTO> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/my-completion`);
}

/** Roster's completion matrix (manager). */
export async function fetchCompletionReport(cohortId: string): Promise<CompletionReportDTO> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion-report`);
}

/** A cohort's effective config (to load the editor). */
export async function fetchCohortCompletionConfig(cohortId: string): Promise<CompletionConfigDTO> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion-config`);
}

/** Sets (config) or clears (null) a cohort's rules. */
export async function saveCohortCompletionConfig(
  cohortId: string,
  config: CompletionConfig | null,
): Promise<{ ok: boolean; configured: boolean }> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion-config`, {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
}

/** Sets (config) or clears (null) a course's rules (template). */
export async function saveCourseCompletionConfig(
  courseId: string,
  config: CompletionConfig | null,
): Promise<{ ok: boolean; configured: boolean }> {
  return apiJson(`/api/courses/${enc(courseId)}/completion-config`, {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
}

/** Manually marks/unmarks a student as complete. */
export async function markCompletion(
  cohortId: string,
  userId: string,
  complete: boolean,
): Promise<{ ok: boolean }> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion/mark`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, complete }),
  });
}
